import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Checkbox, Input, Modal, Pagination, Select, Spin, Table, Tag, message, Tooltip } from "antd";
import { FiEye } from "react-icons/fi";
import {
    FiSearch,
    FiFilter,
    FiDownload,
    FiRefreshCw,
    FiCheck,
    FiX,
    FiFileText,
    FiCheckCircle,
    FiClock,
    FiArrowUp,
    FiArrowDown,
    FiMessageSquare
} from "react-icons/fi";
import { BsLightningCharge } from "react-icons/bs";
import { FaFilePdf, FaRegImage } from "react-icons/fa";
import * as XLSX from "xlsx";
import { getAccessToken } from "../../services/authApi";
import { downloadAttachmentById } from "../../services/documentApi";
import ConditionComments from "../../components/conditions/ConditionComments";
import "../../components/conditions/ConditionComments.css";
import "./recommendeddocreview.css";

const { getConditions, getAttachments, getEfolderDocumentSettings } = require("../../services/loanApi");

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;

const CORRECT_CONDITION_STATUS = process.env.REACT_APP_DOCREVIEW_CONDITION_STATUS_CORRECT || "";

const MAPPING_STATUS_OPTIONS = [
    "Requested",
    "Re-requested",
    "Fulfilled",
    "Received",
    "Reviewed",
    "Rejected",
    "Cleared",
    "Waived"
];


const resolveApiBaseUrl = () => {
    if (API_DOMAIN) {
        return `${API_DOMAIN}/api/recommended-doc-review`;
    }

    return "/api/recommended-doc-review";
};

const getValue = (item, keys, fallback = "") => {
    for (const key of keys) {
        if (item?.[key] !== undefined && item?.[key] !== null) {
            return item[key];
        }
    }

    return fallback;
};

const formatUploadedDate = (rawValue) => {
    if (!rawValue) {
        return "-";
    }

    const parsed = new Date(rawValue);
    if (Number.isNaN(parsed.getTime())) {
        return String(rawValue);
    }

    return parsed.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
};

const inferFileType = (fileName, fallbackType) => {
    const incomingType = String(fallbackType || "").toLowerCase();
    if (incomingType === "pdf" || incomingType === "image") {
        return incomingType;
    }

    if (!fileName) {
        return "pdf";
    }

    const normalized = String(fileName).toLowerCase();
    if (normalized.endsWith(".jpg") || normalized.endsWith(".jpeg") || normalized.endsWith(".png") || normalized.endsWith(".gif") || normalized.endsWith(".webp")) {
        return "image";
    }

    return "pdf";
};

const parseBooleanValue = (rawValue) => {
    if (typeof rawValue === "boolean") {
        return rawValue;
    }

    if (typeof rawValue === "number") {
        return rawValue !== 0;
    }

    const normalized = String(rawValue || "").trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
};


const normalizeApiDocument = (item, index) => {
    const documentName = getValue(item, ["documentName", "DocumentName", "fileName", "FileName", "sourceFileName", "SourceFileName"], `Document_${index + 1}.pdf`);
    const createdDateRaw = getValue(item, ["createdDate", "CreatedDate", "uploadedOn", "UploadedOn", "uploadedDate", "UploadedDate", "createdOn", "CreatedOn"]);
    const recommendedEFolder = getValue(item, ["recommendedEFolder", "RecommendedEFolder", "eFolderRecommended", "EFolderRecommended"], "N/A");
    const currentEFolderName = getValue(item, ["currentEFolderName", "CurrentEFolderName"], "");
    const recommendedCondition = getValue(item, ["recommendedConditionName", "RecommendedConditionName", "conditionRecommended", "ConditionRecommended"], "N/A");
    const recommendedConditionId = getValue(item, ["recommendedCondition", "RecommendedCondition", "conditionRecommended", "ConditionRecommended"], "N/A");
    const recommendedReason = getValue(item, ["recommendedReason", "RecommendedReason", "reason", "Reason", "extractionReason", "ExtractionReason"], "No reason provided.");
    const recommendedConfidence = Number(
        (Number(getValue(item, ["recommendedConfidence", "RecommendedConfidence", "confidence", "Confidence"], 0)) || 0).toFixed(3)
    );
    const userAcceptStatus = getValue(item, ["userAcceptStatus", "UserAcceptStatus"], "Pending");
    const conditionId = String(
        getValue(item, ["conditionId", "ConditionId", "actualConditionId", "ActualConditionId"], "")
    ).trim();
    const commentsCount = Number(getValue(item, ["commentsCount", "CommentsCount"], 0)) || 0;
    const isSplitted = parseBooleanValue(getValue(item, ["isSplitted", "IsSplitted"], false));
    return {
        id: String(getValue(item, ["_id", "id", "Id", "documentId", "DocumentId", "recordId", "RecordId"], `DOC-${index + 1}`)),
        loanId: getValue(item, ["loanId", "LoanId"], ""),
        loanNumber: getValue(item, ["loanNumber", "LoanNumber"], ""),
        documentId: String(getValue(item, ["documentId", "DocumentId", "id", "Id"], "")),
        documentName,
        documentContent: getValue(item, ["documentContent", "DocumentContent"], ""),
        recommendedConfidence,
        recommendedEFolder,
        currentEFolderName,
        recommendedCondition,
        recommendedConditionId,
        recommendedReason,
        actualDocumentType: getValue(item, ["actualDocumentType", "ActualDocumentType"], ""),
        actualEFolder: getValue(item, ["actualEFolder", "ActualEFolder"], ""),
        actualCondition: getValue(item, ["actualCondition", "ActualCondition"], ""),
        conditionId,
        commentsCount,
        userAcceptStatus,
        userAcceptReason: getValue(item, ["userAcceptReason", "UserAcceptReason"], ""),
        userAcceptDate: formatUploadedDate(getValue(item, ["userAcceptDate", "UserAcceptDate"])),
        createdDateRaw,
        createdDate: formatUploadedDate(createdDateRaw),
        mappingMatched: getValue(item, ["mappingMatched", "MappingMatched"], false),
        isSplitted,
        // Backward-compatible aliases used by modal/actions.
        fileName: documentName,
        fileType: inferFileType(documentName, getValue(item, ["fileType", "FileType"])),
        uploadedOn: formatUploadedDate(createdDateRaw),
        eFolderRecommended: recommendedEFolder,
        conditionRecommended: recommendedCondition,
        confidence: recommendedConfidence,
        reason: recommendedReason
    };
};

const withTimeout = (promise, timeoutMs, timeoutMessage) => {
    return Promise.race([
        promise,
        new Promise((_, reject) => {
            setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
        })
    ]);
};

const readJsonSafe = async (response) => {
    try {
        return await response.json();
    } catch {
        return null;
    }
};

const getErrorMessage = (error, fallback) => {
    if (error?.message) {
        return error.message;
    }

    return fallback;
};


const getConfidencePercentValue = (rawConfidence) => {
    const numeric = Number(rawConfidence);
    if (!Number.isFinite(numeric) || numeric <= 0) {
        return 0;
    }

    return numeric <= 1 ? numeric * 100 : numeric;
};

const formatConfidencePercent = (rawConfidence) => `${Math.round(getConfidencePercentValue(rawConfidence))}`;

const resolveStatusTabKey = (statusValue) => {
    const normalized = String(statusValue || "").trim().toLowerCase();

    if (normalized === "processing") {
        return "processing";
    }

    if (normalized === "pending") {
        return "pending";
    }

    if (normalized === "correct" || normalized === "accepted" || normalized === "approve") {
        return "correct";
    }

    if (normalized === "corrected" || normalized === "rejected" || normalized === "reject") {
        return "corrected";
    }

    return "processing";
};

const canRenderInStatusTab = (item, tabKey) => {
    const resolvedTabKey = resolveStatusTabKey(item?.userAcceptStatus);
    if (resolvedTabKey !== tabKey) {
        return false;
    }

    if (tabKey === "processing") {
        return item?.isSplitted === false;
    }

    return true;
};

const isExcludedFromCounts = (item) => {
    return resolveStatusTabKey(item?.userAcceptStatus) === "processing" && item?.isSplitted === true;
};

const getUserStatusTone = (statusValue) => {
    const normalized = String(statusValue || "").trim().toLowerCase();

    if (normalized === "correct") {
        return "correct";
    }

    if (normalized === "corrected") {
        return "corrected";
    }

    if (normalized === "processing") {
        return "processing";
    }

    return "pending";
};

const buildConicGradient = (segments) => {
    const total = segments.reduce((sum, item) => sum + item.value, 0);
    if (!total) {
        return "conic-gradient(#e5e7eb 0deg 360deg)";
    }
    let cursor = 0;

    return `conic-gradient(${segments
        .map((item) => {
            const start = (cursor / total) * 360;
            cursor += item.value;
            const end = (cursor / total) * 360;
            return `${item.color} ${start}deg ${end}deg`;
        })
        .join(", ")})`;
};

const StatCard = ({ title, value, hint, icon, tone = "blue", trend = "up" }) => {
    const trendIcon = trend === "up" ? <FiArrowUp /> : <FiArrowDown />;

    return (
        <div className={`doc-review-stat-card ${tone}`}>
            <div className="doc-review-stat-head">{title}</div>
            <div className="doc-review-stat-main">
                <div className="doc-review-stat-value">{value}</div>
                <div className="doc-review-stat-icon">{icon}</div>
            </div>
            <div className="doc-review-stat-foot">
                {hint ? (
                    <>
                        {trendIcon}
                        <span>{hint}</span>
                    </>
                ) : (
                    <span>&nbsp;</span>
                )}
            </div>
        </div>
    );
};

const DonutChartCard = ({ title, segments, valueFormatter }) => (
    <div className="doc-review-chart-card">
        <h3>{title}</h3>
        <div className="doc-review-donut-layout">
            <div
                className="doc-review-donut"
                style={{ background: buildConicGradient(segments) }}
            />
            <div className="doc-review-legend">
                {segments.map((segment) => (
                    <div key={segment.label} className="doc-review-legend-row">
                        <span
                            className="doc-review-legend-dot"
                            style={{ backgroundColor: segment.color }}
                        />
                        <span className="doc-review-legend-label">{segment.label}</span>
                        <span className="doc-review-legend-value">
                            {valueFormatter ? valueFormatter(segment.value) : segment.value}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const AccuracyTrendCard = ({ points, labels }) => {
    const safePoints = Array.isArray(points) && points.length ? points : [0, 0, 0, 0, 0, 0, 0];
    const safeLabels = Array.isArray(labels) && labels.length ? labels : ["-", "-", "-", "-", "-", "-", "-"];
    const width = 380;
    const height = 130;
    const min = 0;
    const max = 100;

    const denominator = safePoints.length > 1 ? safePoints.length - 1 : 1;
    const linePoints = safePoints
        .map((point, index) => {
            const x = (index / denominator) * width;
            const y = height - ((point - min) / (max - min)) * height;
            return `${x},${y}`;
        })
        .join(" ");

    return (
        <div className="doc-review-chart-card">
            <h3>Accuracy Trend (This Loan)</h3>
            <div className="doc-review-trend-layout">
                <div className="doc-review-trend-grid">
                    <span>100%</span>
                    <span>50%</span>
                    <span>0%</span>
                </div>
                <svg viewBox={`0 0 ${width} ${height}`} className="doc-review-trend-svg" role="img" aria-label="Accuracy trend chart">
                    <polyline points={linePoints} fill="none" stroke="#2f6fed" strokeWidth="3" />
                </svg>
            </div>
            <div className="doc-review-trend-dates">
                {safeLabels.map((label) => (
                    <span key={label}>{label}</span>
                ))}
            </div>
        </div>
    );
};

const RecommendedDocReview = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const [searchValue, setSearchValue] = useState("");
    const [activeTab, setActiveTab] = useState("Pending");
    const [currentPage, setCurrentPage] = useState(1);
    const [documents, setDocuments] = useState([]);
    const [accessToken, setAccessToken] = useState("");
    const [currentLoanId, setCurrentLoanId] = useState("");
    const [reasonModalRecord, setReasonModalRecord] = useState(null);
    const [openCommentsRecord, setOpenCommentsRecord] = useState(null);
    const [mappingModalRecord, setMappingModalRecord] = useState(null);
    const [loanNumber, setLoanNumber] = useState("");
    const [loanID, setLoanID] = useState("");
    const [isGridLoading, setIsGridLoading] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isSubmittingAction, setIsSubmittingAction] = useState(false);
    const [submittingActionRecordId, setSubmittingActionRecordId] = useState("");
    const [mappingForm, setMappingForm] = useState({
        actualEFolder: "",
        actualCondition: "",
        actualConditionId: "",
        status: "",
        comments: ""
    });

    const pageSize = 5;
    const [viewRecord, setViewRecord] = useState(null);
    const [actualConditions, setActualConditions] = useState([]);

    const [actualEFolderOptions, setActualEFolderOptions] = useState([]);

    const resolveEFolderOptionsFromApi = (efolderMaster) => {
        return (Array.isArray(efolderMaster) ? efolderMaster : [])
            .map((item) => ({
                title: String(item?.name ?? item?.Title ?? "").trim(),
                description: String(item?.description ?? item?.Description ?? "").trim()
            }))
            .filter((item) => item.title);
    };


    const getAuthCode = async () => {
        const authObj = await window.elli.script.getObject("auth");
        return authObj.createAuthCode();
    };

    const getToken = useCallback(async () => {
        const authCode = await getAuthCode();
        const token = await getAccessToken(authCode);
        if (!token) throw new Error("Unable to acquire access token");
        return token;
    }, []);


    const getActualEFolderOptions = () => actualEFolderOptions.length ? actualEFolderOptions : [];
    const fetchByLoanNumber = async (loanNum, loanId) => {
        if (!loanNum) {
            return;
        }

        setIsGridLoading(true);
        let payload = null;

        try {
            const apiUrl = `${resolveApiBaseUrl()}/loan/${encodeURIComponent(loanNum)}`;
            const response = await fetch(apiUrl, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json"
                }
            });

            payload = await readJsonSafe(response);
            if (!response.ok) {
                throw new Error(payload?.message || "Failed to fetch recommended documents.");
            }

            const collection = Array.isArray(payload?.data)
                ? payload.data
                : Array.isArray(payload)
                    ? payload
                    : Array.isArray(payload?.documents)
                        ? payload.documents
                        : Array.isArray(payload?.items)
                            ? payload.items
                            : [];

            const normalized = collection.map((item, index) => normalizeApiDocument(item, index));
            setDocuments(normalized);
            if (normalized.length > 0 && normalized[0]?.loanId) {
                setCurrentLoanId(String(normalized[0].loanId));
            }
        } catch (err) {
            console.error("Failed loading recommended docs:", err);
            messageApi.error(getErrorMessage(err, "Unable to load recommended documents."));
            setDocuments([]);
        }

        try {
            const efolderSettings = await getEFolderSettings();
            const apiEFolderOptions = resolveEFolderOptionsFromApi(
                Array.isArray(efolderSettings) && efolderSettings.length ? efolderSettings : []
            );
            setActualEFolderOptions(apiEFolderOptions);
        } catch (err) {
            console.error("Failed loading eFolder options:", err);
            messageApi.error(getErrorMessage(err, "Unable to load eFolder options."));
            setActualEFolderOptions(resolveEFolderOptionsFromApi([]));
        }

        try {
            const { conditions: conds } = await getLoanCondition(loanId);
            setActualConditions(
                (conds || []).map((c) => ({
                    id: c.id,
                    title: c.title,
                    commentsCount: Number(c.commentsCount || 0)
                }))
            );
        } catch (err) {
            console.error("Failed loading conditions:", err);
            messageApi.error(getErrorMessage(err, "Unable to load conditions."));
            setActualConditions([]);
        } finally {
            setIsGridLoading(false);
        }
    };

    useEffect(() => {
        const boot = async () => {
            try {
                if (window.elli?.script?.guest) {
                    window.elli.script.guest.create();
                    const loanObject = await window.elli.script.getObject("loan");
                    const loan = await loanObject.all();
                    const number = loan?.loan?.loanNumber || loan?.loanNumber || "";
                    console.log("Fetched loan number from Encompass (RecommendedDocReview):", number);
                    setLoanNumber(number || "");
                    setLoanID(loan?.loan?.id || (loan ? loan.id : ""));
                    await fetchByLoanNumber(number, loan?.loan?.id || (loan ? loan?.id : ""));
                } else {
                    console.warn("elli.script not available");
                    messageApi.warning("Encompass context is unavailable. Some data may not load.");
                }
            } catch (error) {
                console.error("Failed to initialize page:", error);
                messageApi.error(getErrorMessage(error, "Unable to initialize document review page."));
            } finally {
                setIsPageLoading(false);
            }
        };
        boot();
    }, []);

    const tabCounts = useMemo(() => {
        const counts = { Processing: 0, Pending: 0, Correct: 0, Corrected: 0 };

        documents.forEach((item) => {
            const tabKey = resolveStatusTabKey(item?.userAcceptStatus);
            if (!canRenderInStatusTab(item, tabKey)) {
                return;
            }

            if (tabKey === "processing") {
                counts.Processing += 1;
                return;
            }

            if (tabKey === "pending") {
                counts.Pending += 1;
                return;
            }

            if (tabKey === "correct") {
                counts.Correct += 1;
                return;
            }

            if (tabKey === "corrected") {
                counts.Corrected += 1;
            }
        });

        return counts;
    }, [documents]);

    const filteredDocuments = useMemo(() => {
        const activeTabKey = activeTab.toLowerCase();
        let result = documents.filter((item) => canRenderInStatusTab(item, activeTabKey));

        if (searchValue.trim()) {
            const normalized = searchValue.toLowerCase();
            result = result.filter((item) => {
                return (
                    String(item.documentName || "").toLowerCase().includes(normalized) ||
                    String(item.recommendedEFolder || "").toLowerCase().includes(normalized) ||
                    String(item.documentClassification || "").toLowerCase().includes(normalized)
                );
            });
        }

        return result;
    }, [searchValue, documents, activeTab]);

    const totalDocumentsMetrics = useMemo(() => {
        const eligibleDocuments = documents.filter((item) => !isExcludedFromCounts(item));

        const totalCount = eligibleDocuments.length;
        const now = Date.now();
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
        const currentWindowStart = now - sevenDaysMs;
        const previousWindowStart = now - (sevenDaysMs * 2);

        let currentWindowCount = 0;
        let previousWindowCount = 0;

        eligibleDocuments.forEach((item) => {
            const sourceDate = item.createdDateRaw || item.createdDate;
            const timestamp = new Date(sourceDate).getTime();

            if (Number.isNaN(timestamp)) {
                return;
            }

            if (timestamp >= currentWindowStart && timestamp <= now) {
                currentWindowCount += 1;
                return;
            }

            if (timestamp >= previousWindowStart && timestamp < currentWindowStart) {
                previousWindowCount += 1;
            }
        });

        const delta = currentWindowCount - previousWindowCount;
        const deltaPrefix = delta >= 0 ? "+" : "";

        return {
            totalCount,
            delta,
            deltaLabel: `${deltaPrefix}${delta} vs last 7 days`,
            trend: delta >= 0 ? "up" : "down"
        };
    }, [documents]);

    const aiAccuracyMetrics = useMemo(() => {
        const now = Date.now();
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
        const currentWindowStart = now - sevenDaysMs;
        const previousWindowStart = now - (sevenDaysMs * 2);

        const isCorrectStatus = (statusValue) =>
            String(statusValue || "").trim().toLowerCase() === "correct";

        const totalCount = documents.length;
        const correctCount = documents.filter((item) => isCorrectStatus(item.userAcceptStatus)).length;
        const currentAccuracy = totalCount ? (correctCount / totalCount) * 100 : 0;

        let currentWindowTotal = 0;
        let currentWindowCorrect = 0;
        let previousWindowTotal = 0;
        let previousWindowCorrect = 0;

        documents.forEach((item) => {
            const sourceDate = item.createdDateRaw || item.createdDate;
            const timestamp = new Date(sourceDate).getTime();

            if (Number.isNaN(timestamp)) {
                return;
            }

            const isCorrect = isCorrectStatus(item.userAcceptStatus);

            if (timestamp >= currentWindowStart && timestamp <= now) {
                currentWindowTotal += 1;
                if (isCorrect) {
                    currentWindowCorrect += 1;
                }
                return;
            }

            if (timestamp >= previousWindowStart && timestamp < currentWindowStart) {
                previousWindowTotal += 1;
                if (isCorrect) {
                    previousWindowCorrect += 1;
                }
            }
        });

        const currentWindowAccuracy = currentWindowTotal ? (currentWindowCorrect / currentWindowTotal) * 100 : 0;
        const previousWindowAccuracy = previousWindowTotal ? (previousWindowCorrect / previousWindowTotal) * 100 : 0;
        const delta = currentWindowAccuracy - previousWindowAccuracy;
        const deltaPrefix = delta >= 0 ? "+" : "";

        return {
            accuracy: currentAccuracy,
            delta,
            deltaLabel: `${deltaPrefix}${delta.toFixed(1)}% vs last 7 days`,
            trend: delta >= 0 ? "up" : "down"
        };
    }, [documents]);

    const userCorrectionsCount = useMemo(
        () => documents.filter((item) => String(item.userAcceptStatus || "").trim().toLowerCase() === "corrected").length,
        [documents]
    );

    const correctStatusMetrics = useMemo(() => {
        const correctStatuses = new Set(["correct", "accepted", "approve"]);
        const correctCount = documents.filter((item) =>
            correctStatuses.has(String(item.userAcceptStatus || "").trim().toLowerCase())
        ).length;

        const totalDocuments = documents.length;
        const percent = totalDocuments ? Math.round((correctCount / totalDocuments) * 100) : 0;

        return {
            count: correctCount,
            hint: `${percent}% of total docs`
        };
    }, [documents]);

    const pendingReviewMetrics = useMemo(() => {
        const pendingDocuments = documents.filter(
            (item) => String(item.userAcceptStatus || "").trim().toLowerCase() === "pending"
        );

        const now = Date.now();
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
        const currentWindowStart = now - sevenDaysMs;
        const previousWindowStart = now - (sevenDaysMs * 2);

        let currentWindowCount = 0;
        let previousWindowCount = 0;

        pendingDocuments.forEach((item) => {
            const sourceDate = item.createdDateRaw || item.createdDate;
            const timestamp = new Date(sourceDate).getTime();

            if (Number.isNaN(timestamp)) {
                return;
            }

            if (timestamp >= currentWindowStart && timestamp <= now) {
                currentWindowCount += 1;
                return;
            }

            if (timestamp >= previousWindowStart && timestamp < currentWindowStart) {
                previousWindowCount += 1;
            }
        });

        const delta = currentWindowCount - previousWindowCount;
        const deltaPrefix = delta >= 0 ? "+" : "";

        return {
            count: pendingDocuments.length,
            deltaLabel: `${deltaPrefix}${delta} vs last 7 days`,
            trend: delta >= 0 ? "up" : "down"
        };
    }, [documents]);

    const userCorrectionsLabel = useMemo(() => {
        const totalDocuments = documents.length;
        if (!totalDocuments) {
            return "0% of total docs";
        }

        const percent = Math.round((userCorrectionsCount / totalDocuments) * 100);
        return `${percent}% of total docs`;
    }, [documents.length, userCorrectionsCount]);

    const eFolderDistributionSegments = useMemo(() => {
        if (!documents.length) {
            return [];
        }

        const MAX_SEGMENTS = 8;

        const counts = new Map();

        documents.forEach((item) => {
            const eFolderName =
                String(item.actualEFolder || "").trim() ||
                "Not Assigned";

            counts.set(
                eFolderName,
                (counts.get(eFolderName) || 0) + 1
            );
        });

        const palette = [
            "#2f6fed",
            "#45aaf2",
            "#f39c12",
            "#f6c343",
            "#2ecc71",
            "#95a5a6",
            "#1abc9c",
            "#e67e22"
        ];

        const sortedEntries = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
        const topEntries = sortedEntries.slice(0, MAX_SEGMENTS);
        const otherCount = sortedEntries
            .slice(MAX_SEGMENTS)
            .reduce((sum, [, value]) => sum + value, 0);

        const topSegments = topEntries.map(([label, value], index) => ({
            label,
            value,
            color: palette[index % palette.length]
        }));

        if (otherCount > 0) {
            topSegments.push({
                label: "Other",
                value: otherCount,
                color: "#95a5a6"
            });
        }

        return topSegments;
    }, [documents]);

    const eFolderDistributionTotal = useMemo(
        () => eFolderDistributionSegments.reduce((sum, segment) => sum + segment.value, 0),
        [eFolderDistributionSegments]
    );


    const reviewOutcomeSegments = useMemo(() => {
        const statusMeta = {
            correct: { label: "Correct", color: "#2ecc71" },
            corrected: { label: "Corrected", color: "#f05d5e" },
            pending: { label: "Pending", color: "#bdc3c7" },
            "auto-routed": { label: "Auto-Routed", color: "#f5b93a" },
            other: { label: "Other", color: "#95a5a6" }
        };

        if (!documents.length) {
            return [];
        }

        const counts = {
            correct: 0,
            corrected: 0,
            pending: 0,
            "auto-routed": 0,
            other: 0
        };

        documents.forEach((item) => {
            const rawStatus = String(item.userAcceptStatus || "Pending").trim().toLowerCase();
            if (counts[rawStatus] !== undefined) {
                counts[rawStatus] += 1;
                return;
            }

            if (rawStatus === "accepted" || rawStatus === "approve" || rawStatus === "correct") {
                counts.correct += 1;
                return;
            }

            if (rawStatus === "rejected" || rawStatus === "reject") {
                counts.corrected += 1;
                return;
            }

            counts.other += 1;
        });

        return Object.keys(counts)
            .filter((key) => counts[key] > 0)
            .map((key) => ({
                label: statusMeta[key].label,
                value: counts[key],
                color: statusMeta[key].color
            }));
    }, [documents]);

    const reviewOutcomeTotal = useMemo(
        () => reviewOutcomeSegments.reduce((sum, segment) => sum + segment.value, 0),
        [reviewOutcomeSegments]
    );

    const accuracyTrendData = useMemo(() => {
        const days = 7;
        const isCorrectStatus = (statusValue) =>
            String(statusValue || "").trim().toLowerCase() === "correct";

        const endDate = new Date();
        endDate.setHours(0, 0, 0, 0);

        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - (days - 1));

        const buckets = Array.from({ length: days }, (_, index) => {
            const date = new Date(startDate);
            date.setDate(startDate.getDate() + index);
            return { date, total: 0, correct: 0 };
        });

        documents.forEach((item) => {
            const sourceDate = item.createdDateRaw || item.createdDate;
            const timestamp = new Date(sourceDate).getTime();
            if (Number.isNaN(timestamp)) {
                return;
            }

            const itemDate = new Date(timestamp);
            itemDate.setHours(0, 0, 0, 0);

            if (itemDate < startDate || itemDate > endDate) {
                return;
            }

            const dayOffset = Math.floor((itemDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
            if (dayOffset < 0 || dayOffset >= days) {
                return;
            }

            buckets[dayOffset].total += 1;
            if (isCorrectStatus(item.userAcceptStatus)) {
                buckets[dayOffset].correct += 1;
            }
        });

        let previousPoint = 0;
        const points = buckets.map((bucket) => {
            if (!bucket.total) {
                return previousPoint;
            }

            const value = Number(((bucket.correct / bucket.total) * 100).toFixed(1));
            previousPoint = value;
            return value;
        });

        const labels = buckets.map((bucket) =>
            bucket.date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric"
            })
        );

        return { points, labels };
    }, [documents]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchValue, activeTab]);

    const resolveAccessToken = async () => {
        if (accessToken) {
            return accessToken;
        }

        const authObject = await withTimeout(
            window.elli.script.getObject("auth"),
            10000,
            "Timed out while getting auth object from Encompass."
        );
        const authCode = await withTimeout(
            authObject.createAuthCode(),
            10000,
            "Timed out while creating auth code from Encompass."
        );
        const token = await getAccessToken(authCode);
        setAccessToken(token);
        return token;
    };

    const triggerBlobDownload = (blob, fileName) => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = downloadUrl;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(downloadUrl);
    };

    const USE_MOCK_CONDITIONS = false; // Set to true to use mock conditions for testing
    const USE_MOCK_EFOLDER_SETTINGS = false; // Set to true to use mock eFolder settings for testing

    const getEFolderSettings = async () => {
        if (USE_MOCK_EFOLDER_SETTINGS) {
            return [
                { name: "1003 - URLA", description: "1003 URLA" },
                { name: "Credit", description: "Credit and liabilities" },
                { name: "Assets", description: "Asset verification documents" },
                { name: "Identification", description: "Identity and compliance documents" },
                { name: "Disclosures", description: "Loan disclosures and acknowledgements" }
            ];
        }

        const token = await resolveAccessToken();
        return await getEfolderDocumentSettings(token);
    };

    const getLoanCondition = async (loanid) => {
        if (USE_MOCK_CONDITIONS) {
            return {
                conditions: [
                    { id: "dff42d79-90ea-4365-9789-c5dc5201b4b3", title: "Loan - expirate date of documents" },
                    { id: "04e871f2-dca7-4e5b-b9b3-250ab1c0d1b7", title: "Credit - VOM/VOR" },
                    { id: "99766242-bfc8-4962-8302-7204087d8e56", title: "Loan Officer - NMLS and Fee Validation" },
                    { id: "09348f2c-3e18-47eb-ae4c-fb812d0780ae", title: "Income - Tax Returns" },
                    { id: "1c9b6970-04bd-4077-9e77-03863210f7a5", title: "Identification - Unexpired ID" },
                    { id: "A3D0PVV", title: "Title Documentation" }
                ]
            };
        }

        const tpoAuthObject = await window.elli.script.getObject("auth");
        const tokenObj = await tpoAuthObject.getAccessToken();
        const token = tokenObj?.access_token || tokenObj?.token || tokenObj;

        if (!token) {
            return { conditions: [] };
        }

        const conditions = await getConditions(token, loanid);

        return {
            conditions: Array.isArray(conditions) ? conditions : []
        };
    };



    const handleDocumentDownload = async (record) => {
        const attachmentId = String(record?.documentId || record?.id || "")
            .replace(/^"+|"+$/g, "")
            .replace(/,+$/g, "")
            .trim();
        const loanIdToUse = String(record?.loanId || currentLoanId || "").trim();

        if (!attachmentId) {
            messageApi.error("Attachment ID is missing for this document.");
            return;
        }

        if (!loanIdToUse) {
            messageApi.error("Loan ID is missing for this document.");
            return;
        }

        if (!window.elli?.script?.getObject) {
            messageApi.error("Encompass context is unavailable for download.");
            return;
        }

        let hideLoading;
        try {
            hideLoading = messageApi.loading("Downloading document...", 0);
            const token = await resolveAccessToken();
            const blob = await downloadAttachmentById(token, loanIdToUse, attachmentId);
            const preferredFileName = String(record?.documentName || record?.fileName || `attachment-${attachmentId}.pdf`).trim();
            triggerBlobDownload(blob, preferredFileName || `attachment-${attachmentId}.pdf`);
            messageApi.success("Document downloaded successfully.");
        } catch (error) {
            console.error("Attachment download failed:", error);
            messageApi.error(error?.message || "Unable to download attachment.");
        } finally {
            if (hideLoading) {
                hideLoading();
            }
        }
    };

    const handleExportToExcel = () => {
        if (!filteredDocuments.length) {
            messageApi.warning("No records available to export.");
            return;
        }

        const rowsForExport = filteredDocuments.map((item) => ({
            "Document Name": item.documentName,
            "Created Date": item.createdDate,
            "Recommended eFolder": item.recommendedEFolder,
            "Recommended Condition": item.recommendedCondition,
            "Recommended Confidence": `${Number(item.recommendedConfidence || 0).toFixed(3)}%`,
            "Recommended Reason": item.recommendedReason,
            "Actual Type": item.actualDocumentType || "-",
            "Actual eFolder": item.actualEFolder || "-",
            "Actual Condition": item.actualCondition || "-",
            "User Status": item.userAcceptStatus || "-"
        }));

        const worksheet = XLSX.utils.json_to_sheet(rowsForExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Document Review");

        const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
        XLSX.writeFile(workbook, `document-review-${timestamp}.xlsx`);
        messageApi.success("Grid data exported successfully.");
    };

    const handleRefreshGrid = async () => {
        if (!loanNumber) {
            messageApi.warning("Loan number is unavailable. Unable to refresh.");
            return;
        }

        await fetchByLoanNumber(loanNumber, loanID);
        messageApi.success("Grid refreshed successfully.");
    };


    const handleCommentsModalClose = async () => {
        setOpenCommentsRecord(null);

        if (loanNumber) {
            await fetchByLoanNumber(loanNumber, loanID);
        }
    };

    const openMappingModal = (record) => {
        setMappingModalRecord(record);

        setMappingForm({
            actualEFolder: "",
            actualCondition: "",
            actualConditionId: "",
            status: "",
            comments: ""
        });
    };

    const applyRecommendedMappingToForm = (record) => {
        if (!record) {
            return;
        }

        const recommendedEFolder = String(record.recommendedEFolder || "").trim();
        const recommendedCondition = String(record.recommendedCondition || "").trim();

        const matchedCondition = actualConditions.find(
            (item) => String(item?.title || "").trim().toLowerCase() === recommendedCondition.toLowerCase()
        );

        setMappingForm((prev) => ({
            ...prev,
            actualEFolder: recommendedEFolder,
            actualCondition: matchedCondition?.title || recommendedCondition,
            actualConditionId: matchedCondition?.id || ""
        }));
    };

    const handleSaveMapping = async () => {
        if (!mappingModalRecord) {
            return;
        }

        if (isSubmittingAction) {
            return;
        }

        if (!mappingForm.actualEFolder || !mappingForm.actualCondition || !mappingForm.status) {
            messageApi.error("Please complete all required fields.");
            return;
        }

        const resolvedActualEFolder = mappingForm.actualEFolder;
        const resolvedActualCondition = mappingForm.actualCondition;
        const resolvedConditionId = mappingForm.actualConditionId;
        const resolvedUserStatus = "Corrected";
        const conditionStatus = mappingForm.status;
        const resolvedUserReason = mappingForm.comments.trim()
            ? `Comments: ${mappingForm.comments.trim()}`
            : `Status updated to ${mappingForm.status}.`;

        const updatePayload = {
            documentId: mappingModalRecord.id,
            recommended: {
                eFolder: mappingModalRecord.recommendedEFolder,
                condition: mappingModalRecord.conditionRecommended,
                confidence: mappingModalRecord.recommendedConfidence
            },
            actual: {
                eFolder: resolvedActualEFolder,
                condition: resolvedActualCondition,
                conditionId: resolvedConditionId,
                comments: mappingForm.comments.trim(),
                status: conditionStatus
            }
        };

        setIsSubmittingAction(true);
        try {
            const apiUrl = `${resolveApiBaseUrl()}/${encodeURIComponent(mappingModalRecord.id)}`;
            const response = await fetch(apiUrl, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    EFolderRecommended: updatePayload.recommended.eFolder,
                    ConditionRecommended: updatePayload.recommended.condition,
                    ActualEFolder: updatePayload.actual.eFolder,
                    ActualCondition: updatePayload.actual.condition,
                    ConditionId: updatePayload.actual.conditionId,
                    Comments: updatePayload.actual.comments,
                    userAcceptReason: resolvedUserReason,
                    UserAcceptStatus: resolvedUserStatus,
                    ConditionStatus: conditionStatus
                })
            });

            const payload = await readJsonSafe(response);
            if (!response.ok) {
                throw new Error(payload?.message || "Failed to update mapping.");
            }

            setDocuments((prevDocs) =>
                prevDocs.map((doc) => {
                    if (doc.id !== mappingModalRecord.id) {
                        return doc;
                    }

                    return {
                        ...doc,
                        actualEFolder: updatePayload.actual.eFolder,
                        actualCondition: updatePayload.actual.condition,
                        reason: mappingForm.comments.trim()
                            ? `User corrected mapping. ${mappingForm.comments.trim()}`
                            : doc.reason,
                        userAcceptStatus: resolvedUserStatus,
                        conditionStatus: conditionStatus
                    };
                })
            );
            if (loanNumber) {
                await fetchByLoanNumber(loanNumber, loanID);
            }
            setMappingModalRecord(null);
            messageApi.success(payload?.message || "Recommended mapping updated.");
        } catch (err) {
            console.error("Update mapping failed:", err);
            messageApi.error(err.message || "Unable to update recommended mapping.");
        } finally {
            setIsSubmittingAction(false);
        }
    };

    const handleAcceptRecommendation = async (record) => {
        setSubmittingActionRecordId(String(record?.id || ""));
        setIsSubmittingAction(true);
        try {
            const apiUrl = `${resolveApiBaseUrl()}/${encodeURIComponent(record.id)}/status`;
            const response = await fetch(apiUrl, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    conditionId: record.recommendedConditionId || "",
                    userAcceptReason: "User accepted the AI recommendation without changes.",
                    UserAcceptStatus: "Correct",
                    ConditionStatus: CORRECT_CONDITION_STATUS
                })
            });

            const payload = await readJsonSafe(response);
            if (!response.ok) {
                throw new Error(payload?.message || "Failed to update status.");
            }

            setDocuments((prevDocs) =>
                prevDocs.map((doc) => (doc.id === record.id ? { ...doc, userAcceptStatus: "Correct" } : doc))
            );
            if (loanNumber) {
                await fetchByLoanNumber(loanNumber, loanID);
            }
            messageApi.success(payload?.message || "Status updated successfully.");
        } catch (err) {
            console.error("Status update failed:", err);
            messageApi.error(err.message || "Unable to update status.");
        } finally {
            setIsSubmittingAction(false);
            setSubmittingActionRecordId("");
        }
    };

    const totalEntries = filteredDocuments.length;
    const safeCurrentPage = Math.min(currentPage, Math.max(Math.ceil(totalEntries / pageSize), 1));
    const startEntry = totalEntries === 0 ? 0 : (safeCurrentPage - 1) * pageSize + 1;
    const endEntry = totalEntries === 0 ? 0 : Math.min(safeCurrentPage * pageSize, totalEntries);
    const pagedDocuments = filteredDocuments.slice(startEntry - 1, endEntry);

    const columns = [
        {
            title: "Document Name",
            dataIndex: "documentName",
            key: "documentName",
            width: 270,
            render: (value, record) => (
                <div className="doc-review-file-cell">
                    <span className={`doc-review-file-icon ${record.fileType}`}>
                        {record.fileType === "pdf" ? <FaFilePdf /> : <FaRegImage />}
                    </span>
                    <button
                        type="button"
                        className="doc-review-file-name doc-review-file-link"
                        onClick={() => handleDocumentDownload(record)}
                    >
                        {value}
                    </button>
                </div>
            )
        },
        {
            title: "Uploaded On",
            dataIndex: "createdDate",
            key: "createdDate",
            width: 170,
            render: (value) => <span className="doc-review-muted">{value}</span>
        },
        {
            title: <Tooltip title="Recommended eFolder"><span>Rec. eFolder</span></Tooltip>,
            dataIndex: "recommendedEFolder",
            key: "recommendedEFolder",
            width: 190
        },
        {
            title: <Tooltip title="Recommended Condition"><span>Rec. Cond.</span></Tooltip>,
            dataIndex: "recommendedCondition",
            key: "recommendedCondition",
            width: 220
        },
        {
            title: <Tooltip title="Recommended Confidence"><span>Rec. Confidence</span></Tooltip>,
            dataIndex: "recommendedConfidence",
            key: "recommendedConfidence",
            width: 110,
            render: (value) => (
                <Tag
                    className="doc-review-confidence-tag"
                    color={formatConfidencePercent(value) > 90 ? "green" : "orange"}
                >
                    {formatConfidencePercent(value)}%
                </Tag>
            )
        },
        {
            title: <Tooltip title="Recommended Reason"><span>Rec. Reason</span></Tooltip>,
            dataIndex: "recommendedReason",
            key: "recommendedReason",
            width: 290,
            render: (value, record) => {
                const reasonText = String(value || "").trim();
                const shouldShowViewMore = reasonText.length > 50;

                return (
                    <div className="doc-review-reason-cell">
                        <span className="doc-review-reason-clamp">{reasonText || "-"}</span>
                        {shouldShowViewMore ? (
                            <button
                                type="button"
                                className="doc-review-link-btn"
                                onClick={() => setReasonModalRecord(record)}
                            >
                                View more
                            </button>
                        ) : null}
                    </div>
                );
            }
        },
        {
            title: "Actual EFolder",
            dataIndex: "actualEFolder",
            key: "actualEFolder",
            width: 220
        },
        {
            title: "Actual Condition",
            dataIndex: "actualCondition",
            key: "actualCondition",
            width: 220
        },
        {
            title: "User Status",
            dataIndex: "userAcceptStatus",
            key: "userAcceptStatus",
            width: 120,
            render: (value) => {
                const label = value || "Pending";
                const tone = getUserStatusTone(label);

                return (
                    <span className={`doc-review-user-status-pill ${tone}`}>
                        {label}
                    </span>
                );
            }
        },
        {
            title: "Comments",
            key: "comments",
            width: 90,
            render: (_, record) => {
                const actualConditionName = String(record?.actualCondition || "").trim().toLowerCase();
                const matchedCondition = actualConditions.find(
                    (item) => String(item?.title || "").trim().toLowerCase() === actualConditionName
                );
                const matchedConditionId = matchedCondition?.id || "";
                const resolvedConditionId = String(matchedConditionId || matchedConditionId || "").trim();
                const hasActualCondition = Boolean(resolvedConditionId);
                const commentsCount = Number(matchedCondition?.commentsCount ?? 0) || 0;
                const isOpen = openCommentsRecord?.id === record?.id;

                return (
                    <div
                        className={`doc-review-comments-trigger ${hasActualCondition ? "is-clickable" : ""}`}
                        onClick={() => {
                            if (!hasActualCondition) {
                                return;
                            }



                            if (!resolvedConditionId) {
                                messageApi.warning("Condition ID is unavailable for comments.");
                                return;
                            }

                            setOpenCommentsRecord((prev) => (prev?.id === record?.id ? null : {
                                ...record,
                                conditionId: resolvedConditionId
                            }));
                        }}
                        role={hasActualCondition ? "button" : undefined}
                        aria-label="View comments"
                        title={hasActualCondition ? "View comments" : "No comments"}
                    >
                        <FiMessageSquare className={`doc-review-comments-icon ${isOpen ? "is-open" : ""}`} />
                        {hasActualCondition ? <span className="doc-review-comments-badge">{commentsCount}</span> : null}
                    </div>
                );
            }
        },
        {
            title: "User Action",
            key: "userAction",
            width: 150,
            render: (_, record) => {
                const isPending = getUserStatusTone(record?.userAcceptStatus) === "pending";
                const isrecommendedEFolderAvailable = String(record?.recommendedEFolder || "").trim() !== "";
                const isrecommendedConditionAvailable = String(record?.recommendedCondition || "").trim() !== "";
                const isMappingMatched = record?.mappingMatched === true;
                const isRowActionLoading = isSubmittingAction && submittingActionRecordId === String(record?.id || "");
                return (
                    <div className="doc-review-action-group">
                        <button
                            type="button"
                            className="doc-review-action download"
                            aria-label="Download document"
                            onClick={() => handleDocumentDownload(record)}
                        >
                            <FiDownload />
                        </button>
                        {isPending && (isrecommendedEFolderAvailable && isrecommendedConditionAvailable && isMappingMatched) ? (
                            <>
                                <button
                                    type="button"
                                    className="doc-review-action approve"
                                    aria-label="Approve recommendation"
                                    onClick={() => handleAcceptRecommendation(record)}
                                    disabled={isSubmittingAction}
                                >
                                    {isRowActionLoading ? <Spin size="small" /> : <FiCheck />}
                                </button>
                            </>
                        ) : null}
                        {/* View and Cross */}
                        {isPending && (
                            <button
                                type="button"
                                className="doc-review-action reject"
                                aria-label="Reject recommendation"
                                onClick={() => openMappingModal(record)}
                                disabled={isSubmittingAction}
                            >
                                <FiX />
                            </button>
                        )}
                        <button
                            type="button"
                            className="doc-review-action view"
                            aria-label="View Details"
                            onClick={() => setViewRecord(record)}
                        >
                            <FiEye />
                        </button>
                    </div>
                );
            }
        }
    ];

    if (isPageLoading) {
        return (
            <div className="doc-review-page">
                {contextHolder}
                <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Spin size="large" tip="Loading document review..." />
                </div>
            </div>
        );
    }

    return (
        <div className="doc-review-page">
            {contextHolder}
            <header className="doc-review-header">
                <div className="doc-review-header-copy">
                    <p>Review and validate AI recommendations for this loan</p>
                </div>
            </header>

            <section className="doc-review-stat-grid">
                <StatCard
                    title="Total Documents"
                    value={totalDocumentsMetrics.totalCount}
                    hint={totalDocumentsMetrics.deltaLabel}
                    icon={<FiFileText />}
                    tone="blue"
                    trend={totalDocumentsMetrics.trend}
                />
                <StatCard
                    title="AI Accuracy (This Loan)"
                    value={`${aiAccuracyMetrics.accuracy.toFixed(1)}%`}
                    hint={aiAccuracyMetrics.deltaLabel}
                    icon={<FiCheckCircle />}
                    tone="green"
                    trend={aiAccuracyMetrics.trend}
                />
                <StatCard
                    title="Correct"
                    value={correctStatusMetrics.count}
                    hint={correctStatusMetrics.hint}
                    icon={<BsLightningCharge />}
                    tone="amber"
                    trend="up"
                />
                <StatCard
                    title="Pending Review"
                    value={pendingReviewMetrics.count}
                    hint={pendingReviewMetrics.deltaLabel}
                    icon={<FiClock />}
                    tone="violet"
                    trend={pendingReviewMetrics.trend}
                />
                <StatCard
                    title="User Corrections"
                    value={userCorrectionsCount}
                    hint={userCorrectionsLabel}
                    icon={null}
                    tone="slate"
                    trend="up"
                />
            </section>

            <section className="doc-review-table-wrap">
                <div className="doc-review-tabs">
                    {["Processing", "Pending", "Correct", "Corrected"].map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            className={`doc-review-tab ${tab.toLowerCase()} ${activeTab === tab ? "active" : ""}`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab}
                            <span className="doc-review-tab-count">{tabCounts[tab]}</span>
                        </button>
                    ))}
                </div>
                <div className="doc-review-table-head">
                    <h2>
                        Document Review <span className="doc-review-chip">{filteredDocuments.length}</span>
                    </h2>
                    <div className="doc-review-controls">
                        {/* {loanNumber ? <span className="doc-review-loan-pill">Loan #{loanNumber}</span> : null} */}
                        <Input
                            allowClear
                            placeholder="Search documents..."
                            prefix={<FiSearch />}
                            value={searchValue}
                            onChange={(event) => setSearchValue(event.target.value)}
                            className="doc-review-search"
                        />
                        <Button
                            icon={<FiDownload />}
                            onClick={handleExportToExcel}
                            className="bg-[#006aa9] text-white font-semibold py-2 px-4 rounded-md hover:bg-[#E50956] flex items-center"
                        >
                            Export
                        </Button>
                        <Button
                            icon={<FiRefreshCw />}
                            onClick={handleRefreshGrid}
                            loading={isGridLoading}
                            className="bg-[#006aa9] text-white font-semibold py-2 px-4 rounded-md hover:bg-[#E50956] flex items-center"
                        >

                        </Button>
                    </div>
                </div>

                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={pagedDocuments}
                    pagination={false}
                    loading={isGridLoading}
                    scroll={{ x: 2450 }}
                    className="doc-review-table"
                />
                <div className="doc-review-table-footer">
                    <span className="doc-review-table-summary">
                        Showing {startEntry} to {endEntry} of {totalEntries} entries
                    </span>
                    <Pagination
                        current={safeCurrentPage}
                        pageSize={pageSize}
                        total={totalEntries}
                        onChange={(page) => setCurrentPage(page)}
                        showSizeChanger={false}
                    />
                </div>
            </section>

            <section className="doc-review-chart-grid" style={{ display: "none" }}>
                <AccuracyTrendCard points={accuracyTrendData.points} labels={accuracyTrendData.labels} />
                <DonutChartCard
                    title="EFolder Distribution"
                    segments={eFolderDistributionSegments}
                    valueFormatter={(value) => {
                        const denominator = eFolderDistributionTotal || 1;
                        return `${value} (${Math.round((value / denominator) * 100)}%)`;
                    }}
                />
                <DonutChartCard
                    title="Review Outcome"
                    segments={reviewOutcomeSegments}
                    valueFormatter={(value) => {
                        const denominator = reviewOutcomeTotal || 1;
                        return `${value} (${Math.round((value / denominator) * 100)}%)`;
                    }}
                />
            </section>

            <p className="doc-review-footnote">
                AI recommendations are based on Azure AI Document Intelligence and custom mortgage document models.
            </p>

            <Modal
                title="Reason Details"
                open={Boolean(reasonModalRecord)}
                onCancel={() => setReasonModalRecord(null)}
                footer={[
                    <Button key="close" onClick={() => setReasonModalRecord(null)}>
                        Close
                    </Button>
                ]}
            >
                {reasonModalRecord && (
                    <div className="doc-review-modal-content">
                        <p className="doc-review-modal-title">{reasonModalRecord.fileName}</p>
                        <p className="doc-review-modal-subtitle">Confidence: {formatConfidencePercent(reasonModalRecord.confidence)}%</p>
                        <p className="doc-review-modal-text">{reasonModalRecord.reason}</p>
                    </div>
                )}
            </Modal>

            <Modal
                title="Update Recommended Mapping"
                open={Boolean(mappingModalRecord)}
                onCancel={() => {
                    if (isSubmittingAction) {
                        return;
                    }
                    setMappingModalRecord(null);
                }}
                closable={!isSubmittingAction}
                maskClosable={!isSubmittingAction}
                keyboard={!isSubmittingAction}
                width={680}
                footer={[
                    <div key="mapping-actions" className="doc-review-modal-footer-actions">
                        <Button
                            onClick={() => {
                                setMappingModalRecord(null);
                            }}
                            disabled={isSubmittingAction}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveMapping}
                            className="doc-review-save-btn bg-[#006aa9] text-white font-semibold py-2 px-4 rounded-md hover:bg-[#E50956] flex items-center"
                            loading={isSubmittingAction}
                            disabled={isSubmittingAction}
                        >
                            Save Changes
                        </Button>
                    </div>
                ]}
            >
                {mappingModalRecord && (
                    <div className="doc-review-modal-form">
                        <div className="doc-review-modal-file">
                            <span className={`doc-review-file-icon ${mappingModalRecord.fileType}`}>
                                {mappingModalRecord.fileType === "pdf" ? <FaFilePdf /> : <FaRegImage />}
                            </span>
                            <div>
                                <div className="doc-review-modal-title">{mappingModalRecord.fileName}</div>
                                <div className="doc-review-modal-subtitle">Uploaded on {mappingModalRecord.uploadedOn}</div>
                            </div>
                        </div>
                        <div className="doc-review-ai-summary">
                            <div className="summary-row">
                                <span className="summary-label">eFolder</span>
                                <span className="summary-value">
                                    {mappingModalRecord.recommendedEFolder}
                                </span>
                            </div>

                            <div className="summary-row">
                                <span className="summary-label">Condition</span>
                                <span className="summary-value">
                                    {mappingModalRecord.recommendedCondition}
                                </span>
                            </div>

                            <div className="summary-row">
                                <span className="summary-label">Confidence</span>
                                <span className="summary-value">
                                    <Tag color={formatConfidencePercent(mappingModalRecord.recommendedConfidence) > 90 ? "green" : "orange"}>
                                        {formatConfidencePercent(mappingModalRecord.recommendedConfidence)}%
                                    </Tag>
                                </span>
                            </div>
                        </div>

                        <div className="doc-review-form-field">
                            <label>eFolder (Actual) <span>*</span></label>
                            <Select
                                showSearch
                                optionFilterProp="searchText"
                                value={mappingForm.actualEFolder}
                                onChange={(value) =>
                                    setMappingForm((prev) => ({
                                        ...prev,
                                        actualEFolder: value
                                    }))
                                }
                                options={actualEFolderOptions.map((item) => ({
                                    value: item.title,
                                    searchText: item.title,
                                    label: (
                                        <div>
                                            <div>{item.title}</div>
                                            {/* <small style={{ color: "#888" }}>
                                                {item.description}
                                            </small> */}
                                        </div>
                                    )
                                }))}
                                placeholder="Select eFolder"
                            />
                        </div>

                        <div className="doc-review-form-field">
                            <label>Condition (Actual) <span>*</span></label>
                            <Select
                                showSearch
                                optionFilterProp="searchText"
                                value={mappingForm.actualConditionId}
                                onChange={(value) =>
                                    setMappingForm((prev) => ({
                                        ...prev,
                                        actualCondition: actualConditions.find((x) => x.id === value)?.title || "",
                                        actualConditionId: actualConditions.find((x) => x.id === value)?.id || ""
                                    }))
                                }
                                options={actualConditions.map((item) => ({
                                    value: item.id,
                                    searchText: item.title,
                                    label: item.title
                                }))}
                                placeholder="Select condition"
                            />
                        </div>

                        <div className="doc-review-form-field">
                            <label>Status <span>*</span></label>
                            <Select
                                showSearch
                                optionFilterProp="label"
                                value={mappingForm.status || undefined}
                                onChange={(value) =>
                                    setMappingForm((prev) => ({
                                        ...prev,
                                        status: value
                                    }))
                                }
                                options={MAPPING_STATUS_OPTIONS.map((item) => ({
                                    value: item,
                                    label: item
                                }))}
                                placeholder="Select status"
                            />
                        </div>

                        <div className="doc-review-form-field">
                            <label>Comments (optional)</label>
                            <Input.TextArea
                                rows={3}
                                value={mappingForm.comments}
                                onChange={(event) => setMappingForm((prev) => ({ ...prev, comments: event.target.value }))}
                                placeholder="Enter notes for this mapping update"
                            />
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                open={Boolean(openCommentsRecord)}
                onCancel={handleCommentsModalClose}
                closable={false}
                footer={null}
                width={760}
                destroyOnClose
            >
                {openCommentsRecord ? (
                    <ConditionComments
                        loanId={String(openCommentsRecord?.loanId || currentLoanId || "")}
                        conditionId={String(openCommentsRecord?.conditionId || "")}
                        getToken={getToken}
                        onClose={handleCommentsModalClose}
                    />
                ) : null}
            </Modal>


            <Modal
                title="Document Details"
                open={Boolean(viewRecord)}
                onCancel={() => setViewRecord(null)}
                width={800}
                footer={[
                    <Button key="close" onClick={() => setViewRecord(null)}>
                        Close
                    </Button>
                ]}
            >
                {viewRecord && (
                    <div className="doc-review-modal-content">
                        <table className="doc-review-details-table">
                            <tbody>
                                <tr>
                                    <td><strong>Document Name</strong></td>
                                    <td>{viewRecord.documentName}</td>
                                </tr>
                                <tr>
                                    <td><strong>Uploaded On</strong></td>
                                    <td>{viewRecord.createdDate}</td>
                                </tr>

                                <tr>
                                    <td><strong>Recommended eFolder</strong></td>
                                    <td>{viewRecord.recommendedEFolder}</td>
                                </tr>
                                <tr>
                                    <td><strong>Recommended Condition</strong></td>
                                    <td>{viewRecord.recommendedCondition}</td>
                                </tr>
                                <tr>
                                    <td><strong>Confidence</strong></td>
                                    <td>{formatConfidencePercent(viewRecord.recommendedConfidence)}%</td>
                                </tr>
                                <tr>
                                    <td><strong>Reason</strong></td>
                                    <td>{viewRecord.recommendedReason}</td>
                                </tr>
                                <tr>
                                    <td><strong>Actual eFolder</strong></td>
                                    <td>{viewRecord.actualEFolder || "-"}</td>
                                </tr>
                                <tr>
                                    <td><strong>Actual Condition</strong></td>
                                    <td>{viewRecord.actualCondition || "-"}</td>
                                </tr>
                                <tr>
                                    <td><strong>User Status</strong></td>
                                    <td>{viewRecord.userAcceptStatus || "Pending"}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default RecommendedDocReview;
